module.exports = {
	"data": {
		"postBySlug": {
			"html": "<p>The OGC Open Geospatial Consortium is an international member-based organization that develops open standards for the geospatial world through a consensus-based approach.\nThe OGC consensus process brings together a community of geospatial experts sharing a common pursuit: <em>interoperability</em>.</p>\n<h2 id=\"the-importance-of-open-geospatial-standards\"><a href=\"#the-importance-of-open-geospatial-standards\" aria-hidden=\"true\" class=\"anchor\"><svg aria-hidden=\"true\" height=\"16\" version=\"1.1\" viewBox=\"0 0 16 16\" width=\"16\"><path fill-rule=\"evenodd\" d=\"M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z\"></path></svg></a>The importance of open geospatial standards</h2>\n<p>The geospatial world is notable for the wide variance in the types of software, data and systems used. While many other industries may have the challenge of how to standardise on common operating procedures, due to the huge diversity in technologies, data models, licensing, and data formats the challenge in the geospatial industry is greater.\nWithout standards and consistent policy, there would be an even larger degree of fragmentation stemming from the lack of interoperability. Interoperability and information integration are key issues for the geospatial world.\nIn the context of <a href=\"https://inspire.ec.europa.eu/\"><strong>INSPIRE</strong></a>, interoperability is key to allowing the sharing of data between public sector bodies within a given Member State or between a number of Member States.\nThere are currently hundreds of products and thousands of references for OGC standards in use worldwide.\nThere are also community standards which have been defined by extending a <em>baseline</em> standard and become a reference implementation for specific sectors. A good example is in the aviation sector where an application profile of the OGC <strong>Geography Markup Language</strong> (<a href=\"http://www.opengeospatial.org/standards/gml\">GML</a>) called Aeronautical Information Exchange Model (<a href=\"http://www.aixm.aero/public/subsite_homepage/homepage.html\">AIXM</a>) is used by Eurocontrol in Europe and the Federal Aviation Authority (FAA) in North America for interoperability between aviation datasets.</p>\n<blockquote>\n<p>So why don't imagine the same success for <strong>CAAML</strong>?</p>\n</blockquote>\n<p>The OGC actively collaborates with numerous other standards organizations. For example, the OGC has relationships with <a href=\"https://www.iso.org/\">ISO</a>. The bridge with ISO, the International Organization for Standardization, is especially strong as OGC standards are often approved as ISO standards. Meaningful examples are e.g. OGC’s Web Feature Service (<strong>WFS</strong>), Web Mapping Service (<strong>WMS</strong>) and the above <strong>GML</strong> which also entered into ISO standards (ISO 19142, ISO 19128 and ISO 19136).</p>\n<h2 id=\"common-standards\"><a href=\"#common-standards\" aria-hidden=\"true\" class=\"anchor\"><svg aria-hidden=\"true\" height=\"16\" version=\"1.1\" viewBox=\"0 0 16 16\" width=\"16\"><path fill-rule=\"evenodd\" d=\"M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z\"></path></svg></a>Common standards</h2>\n<p>There are a lot of OGC standards which might be potentially interesting for the snow avalanche domain but here we will take into accounts only those related to the visualization of vector and raster datasets within a map and the exchange of their data content.\nIn addition also the exploitation of remote processes and models over the web will be demonstrated through the use of a common way to execute and chained them into pipelines for generating new data on the fly.</p>\n<ul>\n<li>\n<p><a href=\"http://www.opengeospatial.org/standards/gml\">GML - Geography Markup Language</a>\nit is an XML grammar for expressing geographical features.</p>\n<p>GML serves as a modeling language for geographic systems as well as an open interchange format for geographic transactions on the Internet. As with commonly used XML based grammars, there are two parts of the grammar – the schema that describes the document and the instance document that contains the actual data. A GML document is described using a GML Schema. This allows users and developers to describe generic geographic data sets that contain points, lines and polygons.</p>\n</li>\n<li>\n<p><a href=\"http://www.opengeospatial.org/standards/wms\">WMS - Web Mapping Service</a>\nit is a web service specification for rendering information on the maps</p>\n</li>\n<li>\n<p><a href=\"http://www.opengeospatial.org/standards/wfs\">WFS - Web Feature Service</a>\nit is a web service specification for streamlining vector data</p>\n</li>\n<li>\n<p><a href=\"http://www.opengeospatial.org/standards/wcs\">WCS - Web Coverage Service</a>\nit is a web service specification for streamlining grid data</p>\n</li>\n<li>\n<p><a href=\"http://www.opengeospatial.org/standards/wps\">WPS - Web Processing Service</a>\nit is a web service specification for running and combining processes</p>\n</li>\n</ul>\n<p>If you want to learn more follow this e-learning course from <a href=\"http://cite.opengeospatial.org/pub/cite/files/edu/index.html\">OGC</a></p>",
			"timeToRead": 3,
			"excerpt": "The OGC Open Geospatial Consortium is an international member-based organization that develops open standards for the geospatial world...",
			"frontmatter": {
				"title": "Introduction to OGC",
				"cover": null,
				"date": null,
				"category": null,
				"tags": null
			}
		},
		"tableOfContents": {
			"chapters": [
				{
					"title": "1. Introduction",
					"entries": null,
					"chapters": [
						{
							"title": "1.1 OGC Standards",
							"entries": [
								{
									"entry": {
										"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/intro_ogc.md absPath of file",
										"childMarkdownRemark": {
											"fields": {
												"slug": "/introduction-to-ogc"
											},
											"frontmatter": {
												"title": "Introduction to OGC"
											}
										}
									}
								}
							],
							"chapters": [
								{
									"title": "1.1.1 Web Mapping Service",
									"entries": [
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/intro_wms.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/introduction-to-wms"
													},
													"frontmatter": {
														"title": "Introduction to WMS"
													}
												}
											}
										},
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/examples_wms.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/wms-examples"
													},
													"frontmatter": {
														"title": "WMS Examples"
													}
												}
											}
										}
									]
								},
								{
									"title": "1.1.2 Web Feature Service",
									"entries": [
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/intro_wfs.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/introduction-to-wfs"
													},
													"frontmatter": {
														"title": "Introduction to WFS"
													}
												}
											}
										},
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/examples_wfs.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/wfs-examples"
													},
													"frontmatter": {
														"title": "WFS Examples"
													}
												}
											}
										}
									]
								},
								{
									"title": "1.1.3 Web Coverage Service",
									"entries": [
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/intro_wcs.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/introduction-to-wcs"
													},
													"frontmatter": {
														"title": "Introduction to WCS"
													}
												}
											}
										},
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/examples_wcs.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/wcs-examples"
													},
													"frontmatter": {
														"title": "WCS Examples"
													}
												}
											}
										}
									]
								},
								{
									"title": "1.1.4 Web Processing Service",
									"entries": [
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/intro_wps.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/introduction-to-wps"
													},
													"frontmatter": {
														"title": "Introduction to WPS"
													}
												}
											}
										},
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/examples_wps.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/wps-examples"
													},
													"frontmatter": {
														"title": "WPS Examples"
													}
												}
											}
										}
									]
								}
							]
						},
						{
							"title": "1.2 CAAML",
							"entries": [
								{
									"entry": {
										"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/intro_caaml.md absPath of file",
										"childMarkdownRemark": {
											"fields": {
												"slug": "/introduction-to-caaml"
											},
											"frontmatter": {
												"title": "Introduction to CAAML"
											}
										}
									}
								}
							],
							"chapters": null
						},
						{
							"title": "1.3 INSPIRE",
							"entries": [
								{
									"entry": {
										"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/intro_inspire.md absPath of file",
										"childMarkdownRemark": {
											"fields": {
												"slug": "/introduction-to-inspire"
											},
											"frontmatter": {
												"title": "Introduction to INSPIRE"
											}
										}
									}
								}
							],
							"chapters": null
						}
					]
				},
				{
					"title": "2. Standards in action",
					"entries": null,
					"chapters": [
						{
							"title": "2.1 Prepare data and toolkit",
							"entries": [
								{
									"entry": {
										"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/intro_data_tools.md absPath of file",
										"childMarkdownRemark": {
											"fields": {
												"slug": "/data-and-geospatial-tools"
											},
											"frontmatter": {
												"title": "Data and geospatial tools"
											}
										}
									}
								},
								{
									"entry": {
										"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/bootstrap_servers.md absPath of file",
										"childMarkdownRemark": {
											"fields": {
												"slug": "/start-the-environment"
											},
											"frontmatter": {
												"title": "Start the environment"
											}
										}
									}
								}
							],
							"chapters": [
								{
									"title": "2.1.1 Manage the data",
									"entries": [
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/load_data.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/load-data"
													},
													"frontmatter": {
														"title": "Load Data"
													}
												}
											}
										},
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/clean_data.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/clean-data"
													},
													"frontmatter": {
														"title": "Clean Data"
													}
												}
											}
										}
									]
								},
								{
									"title": "2.1.2 Design the CAAML schema",
									"entries": [
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/er_design.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/caaml-model-design"
													},
													"frontmatter": {
														"title": "CAAML model design"
													}
												}
											}
										},
										{
											"entry": {
												"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/populate_data.md absPath of file",
												"childMarkdownRemark": {
													"fields": {
														"slug": "/populate-region"
													},
													"frontmatter": {
														"title": "Populate Region"
													}
												}
											}
										}
									]
								}
							]
						},
						{
							"title": "2.2 Build CAAML with GeoAvalanche",
							"entries": [
								{
									"entry": {
										"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/caaml_hale.md absPath of file",
										"childMarkdownRemark": {
											"fields": {
												"slug": "/configure-the-application-schema-for-caaml"
											},
											"frontmatter": {
												"title": "Configure the application schema for CAAML"
											}
										}
									}
								},
								{
									"entry": {
										"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/load_configuration.md absPath of file",
										"childMarkdownRemark": {
											"fields": {
												"slug": "/push-the-configuration-to-geo-avalanche"
											},
											"frontmatter": {
												"title": "Push the configuration to GeoAvalanche"
											}
										}
									}
								},
								{
									"entry": {
										"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/query_data.md absPath of file",
										"childMarkdownRemark": {
											"fields": {
												"slug": "/query-our-region-features"
											},
											"frontmatter": {
												"title": "Query our Region features"
											}
										}
									}
								}
							],
							"chapters": null
						}
					]
				},
				{
					"title": "3. Main Chapter",
					"entries": [
						{
							"entry": {
								"id": "/Users/geobart/code/GeoAvalanche/ogc-caaml-inspire/content/lessons/lesson_six.md absPath of file",
								"childMarkdownRemark": {
									"fields": {
										"slug": "/lesson-six"
									},
									"frontmatter": {
										"title": "Lesson Six"
									}
								}
							}
						}
					],
					"chapters": null
				}
			]
		}
	},
	"pathContext": {
		"slug": "/introduction-to-ogc"
	}
};


//////////////////
// WEBPACK FOOTER
// ./~/json-loader!./.cache/json/introduction-to-ogc.json
// module id = 413
// module chunks = 170398892715585